import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
  MessageCircleQuestionMark,
} from 'lucide-react';
import classNames from 'classnames';
import Link from 'next/link';
import Image from 'next/image';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import { getHelpFaqs } from './constants';
import ContactUsForm from '@/components/help/ContactUsForm';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import i18n from '@/settings/i18n';

const t = i18n.init('help');

const Help = () => {
  const { customer } = useCustomerContext();
  const HELP_FAQS = getHelpFaqs();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeSubIndex, setActiveSubIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    if (activeIndex === index) {
      setActiveIndex(null);
      setActiveSubIndex(null);
    } else {
      setActiveIndex(index);
      setActiveSubIndex(null);
    }
  };

  const toggleSubAccordion = (subIndex: number) => {
    setActiveSubIndex(activeSubIndex === subIndex ? null : subIndex);
  };

  return (
    <div className="bg-white">
      <header className="relative flex items-center bg-gray-700 py-5">
        <div className="reebelo-container w-full md:px-8">
          <h1 className="relative z-10 text-xl font-black uppercase leading-tight text-white">
            {t`Frequently asked`}{' '}
            <span className="block text-teal-500 lg:inline-block">
              {t`questions`}
            </span>
          </h1>
          <div className="absolute right-0 top-0 h-full">
            <Image
              src={getEdgeUrl('images/help/faq.png')}
              height={100}
              width={500}
              alt={t`Frequently Asked Questions`}
              className="h-full w-auto object-cover"
              priority
              quality={100}
            />
          </div>
        </div>
      </header>
      <main className="reebelo-container grid grid-cols-12 gap-6 py-6 lg:py-8">
        <div className="col-span-12 lg:col-span-8 xl:col-span-9">
          {HELP_FAQS.map((faq, index) => {
            const Icon = faq.icon;
            const isOpen = activeIndex === index;

            return (
              <div
                key={`faq_${index}`}
                className="-mb-px overflow-hidden border border-gray-500"
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className={classNames(
                    'group flex w-full items-center justify-between gap-4 p-4 text-left transition-colors hover:bg-[#F8F8F8] focus:outline-none md:p-5',
                    {
                      'bg-[#F8F8F8]': isOpen,
                    },
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="font-bold leading-snug text-gray-700">
                      {faq.sectionTitle}
                    </span>
                  </div>
                  <div
                    className={classNames(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-500 text-gray-700 transition-colors group-hover:bg-gray-700 group-hover:font-semibold group-hover:text-white',
                      {
                        'bg-gray-700 font-semibold text-white': isOpen,
                      },
                    )}
                  >
                    {isOpen ? (
                      <Minus className="h-4 w-4" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </div>
                </button>

                <div
                  className={`grid transition-all duration-200 ease-in-out ${
                    isOpen
                      ? 'grid-rows-[1fr] opacity-100'
                      : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="ml-3 px-3 py-2 md:ml-8 md:px-5 md:py-5">
                      {faq.sectionContent.map((item, subIndex) => {
                        const isSubOpen = activeSubIndex === subIndex;

                        return (
                          <div
                            key={`faq_${index}_sub_${subIndex}`}
                            className="py-3 md:py-5"
                          >
                            <button
                              onClick={() => toggleSubAccordion(subIndex)}
                              className="group flex w-full items-center justify-between gap-4 text-left focus:outline-none"
                            >
                              <h3
                                className={classNames(
                                  'leading-snug group-hover:font-bold',
                                  {
                                    'font-bold': isSubOpen,
                                    'font-normal': !isSubOpen,
                                  },
                                )}
                              >
                                {item.question}
                              </h3>
                              <div
                                className={classNames(
                                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors group-hover:bg-gray-500',
                                  {
                                    'bg-gray-700 text-white group-hover:bg-gray-700':
                                      isSubOpen,
                                  },
                                )}
                              >
                                {isSubOpen ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-5 w-5" />
                                )}
                              </div>
                            </button>
                            <div
                              className={classNames(
                                'grid transition-all duration-200 ease-in-out',
                                {
                                  'mb-4 mt-2 grid-rows-[1fr] opacity-100 last:mb-0':
                                    isSubOpen,
                                  'grid-rows-[0fr] opacity-0': !isSubOpen,
                                },
                              )}
                            >
                              <div className="overflow-hidden">
                                <div className="space-y-2.5 pt-2">
                                  {item.answer}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="col-span-12 flex flex-col gap-6 sm:flex-row lg:col-span-4 lg:flex-col xl:col-span-3">
          <div
            className="relative min-h-[300px] w-full overflow-hidden border border-gray-500 p-8 sm:w-1/2 sm:p-6 lg:w-full"
            // eslint-disable-next-line react/forbid-dom-props
            style={{
              background:
                'radial-gradient(118.2% 123.12% at 14.13% 8.86%, #FAFFFF 33.07%, #92F7FA 100%)',
            }}
          >
            <h2 className="max-w-[200px] text-xl font-bold leading-tight">
              {t`Your Trusted Destination for Affordable & Sustainable Shopping`}
            </h2>
            <div className="absolute inset-0 h-full w-full">
              <Image
                src={getEdgeUrl('images/help/trust.png')}
                height={100}
                width={500}
                alt={t`Frequently Asked Questions`}
                className="h-full w-full object-cover"
                priority
                quality={100}
              />
            </div>
          </div>
          <div className="order-first w-full border border-gray-500 bg-[#F8F8F8] p-6 sm:order-none sm:w-1/2 lg:w-full lg:flex-none">
            <h2 className="flex items-center gap-2 font-bold leading-snug">
              <MessageCircleQuestionMark className="h-6 w-6" />
              {t`Need Help With an Order?`}
            </h2>
            <div className="mt-2 space-y-4 text-sm">
              <p>
                {t`For faster & more personalized help, please contact us directly via`}{' '}
                <Link
                  href="/account/orders"
                  className="underline hover:text-link-blue-500"
                >
                  {t`your customer account`}
                </Link>
                .
              </p>
              <p>
                {t`If you haven't placed any order yet, you can use the form below and our team will get back to you within two business days.`}
              </p>
              <p>
                {t`No account?`}{' '}
                <Link
                  href="/account/register"
                  className="underline hover:text-link-blue-500"
                >
                  {t`Create one`}
                </Link>{' '}
                {t`in a minute to fetch your past orders.`}
              </p>
            </div>
          </div>
        </div>
      </main>
      <div className="-mt-12 lg:-mt-20">
        <ContactUsForm isLoggedIn={!!customer} />
      </div>
    </div>
  );
};

export default Help;
